import { useRef } from 'react';
import type { FocusEvent, MouseEvent } from 'react';

type UseGroupFocusRedirectOptions<T extends HTMLElement> = {
    /** Whether the group takes part in the redirect at all. When false the hook returns no props. */
    enabled: boolean;
    /** Picks the element focus moves to, given the group container. Return `undefined` to leave focus where it is. */
    getTarget: (group: T) => HTMLElement | undefined | null;
};

/**
 * Makes a `role="group"`/`radiogroup` container targetable by an
 * `HBFieldControl` label: the container becomes focusable (`tabIndex={-1}`) and
 * focusing it moves focus to `getTarget(group)`. Spread the returned props on
 * the container.
 *
 * The redirect is skipped when focus comes from a mouse press inside the group.
 * Safari does not focus a checkbox, radio or button on click, so the press
 * focuses this container instead (as does a press on the gap between options),
 * and redirecting would move focus off the item the user just pressed. Radix's
 * roving focus guards against the same thing.
 */
export const useGroupFocusRedirect = <T extends HTMLElement = HTMLDivElement>({
    enabled,
    getTarget
}: UseGroupFocusRedirectOptions<T>) => {
    const isPointerFocusRef = useRef(false);

    if (!enabled) {
        return {};
    }

    return {
        tabIndex: -1,
        onFocus: (event: FocusEvent<T>) => {
            const isPointerFocus = isPointerFocusRef.current;
            isPointerFocusRef.current = false;
            if (event.target !== event.currentTarget || isPointerFocus) {
                return;
            }
            getTarget(event.currentTarget)?.focus();
        },
        onMouseDown: (_event: MouseEvent<T>) => {
            isPointerFocusRef.current = true;
        },
        onMouseUp: (_event: MouseEvent<T>) => {
            isPointerFocusRef.current = false;
        }
    };
};
